import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, Sparkles } from "lucide-react";
import { Button } from "./ui/button";

const STORAGE_KEY = "flowmindai-exit-intent-shown";

/**
 * Exit-intent popup — fires once per user when cursor leaves the top of the viewport.
 * Disabled on small screens (no top-edge gesture).
 */
export default function ExitIntentModal() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.innerWidth < 768) return; // skip mobile
    if (localStorage.getItem(STORAGE_KEY)) return;

    let armed = true;
    const handler = (e) => {
      if (!armed) return;
      // Mouse leaving toward the top of the viewport
      if (e.clientY <= 0 && (e.relatedTarget === null || e.relatedTarget === undefined)) {
        armed = false;
        setOpen(true);
        localStorage.setItem(STORAGE_KEY, new Date().toISOString());
      }
    };

    // Arm only after 10s on the page to avoid early bounce noise
    const armTimer = setTimeout(() => {
      document.addEventListener("mouseout", handler);
    }, 10000);

    return () => {
      clearTimeout(armTimer);
      document.removeEventListener("mouseout", handler);
    };
  }, []);

  const close = () => setOpen(false);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="exit-intent-modal"
          onClick={close}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ type: "spring", stiffness: 280, damping: 24 }}
            className="relative max-w-lg w-full bg-white rounded-sm overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={close}
              className="absolute top-3 right-3 w-9 h-9 rounded-sm flex items-center justify-center text-zinc-500 hover:bg-zinc-100 hover:text-black transition-colors z-10"
              aria-label="Fermer"
              data-testid="exit-intent-close"
            >
              <X size={18} />
            </button>

            <div className="klein-bg text-white px-8 py-6 grain relative overflow-hidden">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
                className="absolute -right-16 -top-16 w-48 h-48 rounded-full opacity-25 pointer-events-none"
                style={{ background: "conic-gradient(from 0deg, #fff, transparent, #fff)" }}
              />
              <div className="relative flex items-center gap-3">
                <div className="w-9 h-9 rounded-sm bg-white/15 backdrop-blur-sm flex items-center justify-center">
                  <Sparkles size={18} />
                </div>
                <p className="font-mono text-xs tracking-widest uppercase">Offre de bienvenue</p>
              </div>
            </div>

            <div className="px-8 py-8">
              <h3 className="font-display text-3xl font-medium tracking-tighter mb-3">
                Attendez ! 14 jours offerts, sans carte bancaire.
              </h3>
              <p className="text-zinc-600 leading-relaxed mb-6">
                Essayez gratuitement <strong>tous les plans</strong> pendant 14 jours.
                Workflows IA illimités, 46 intégrations, support FR. Si vous ne convertissez pas,
                vos workflows restent sauvegardés.
              </p>

              <ul className="space-y-2 mb-6 text-sm">
                <li className="flex items-center gap-2 text-zinc-700">
                  <span className="w-1.5 h-1.5 rounded-full klein-bg" /> Aucune carte bancaire requise
                </li>
                <li className="flex items-center gap-2 text-zinc-700">
                  <span className="w-1.5 h-1.5 rounded-full klein-bg" /> Données hébergées en Europe (RGPD)
                </li>
                <li className="flex items-center gap-2 text-zinc-700">
                  <span className="w-1.5 h-1.5 rounded-full klein-bg" /> Onboarding personnalisé en français
                </li>
              </ul>

              <div className="flex flex-wrap items-center gap-3">
                <Link to="/signup" onClick={close} className="flex-1 min-w-[180px]">
                  <Button
                    className="w-full rounded-sm klein-bg text-white hover:bg-blue-700 h-12"
                    data-testid="exit-intent-cta"
                  >
                    Démarrer mon essai gratuit <ArrowRight size={16} className="ml-2" />
                  </Button>
                </Link>
                <button
                  onClick={close}
                  className="text-sm text-zinc-500 hover:text-black underline px-2"
                  data-testid="exit-intent-decline"
                >
                  Non merci
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
